import { useState, useMemo } from 'react';
import { useToast } from '../context/ToastContext';
import { CLASSROOMS } from '../data/mockData';
import SearchableSelect from '../components/SearchableSelect';

const BLOCK_LABEL = {
  A: 'Block A — A01 to A13 (40 pax: A01–A03 · 10 pax: A04–A13)',
  B: 'Block B — B01 to B11 (all 10 pax)',
  C: 'Block C — C01 to C08 (all 20 pax)',
};
const BLOCK_COL = { A: 'var(--ocean)', B: 'var(--green)', C: 'var(--gold)' };

export default function ClassroomsPage() {
  const { show } = useToast();
  const [block, setBlock] = useState('');
  const [cap, setCap] = useState('');
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    const query = q.toLowerCase();
    return CLASSROOMS.filter((r) =>
      (!block || r.block === block) &&
      (!cap || String(r.cap) === cap) &&
      (!query || `${r.name} ${r.id}`.toLowerCase().includes(query))
    );
  }, [block, cap, q]);

  return (
    <div>
      <div className="sh-ey">RESOURCES · ROOMS, LABS &amp; STUDIOS</div>
      <div className="sh-ti">Classrooms &amp; Facilities</div>
      <div className="sh-de">All teaching rooms across Block A (10–40 pax), Block B (10 pax), and Block C (20 pax). Filter by block or capacity to find the right room for each course.</div>
      <div style={{ display: 'flex', gap: '.75rem', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <SearchableSelect
          className="finput" style={{ width: 160 }} value={block} onChange={setBlock}
          placeholder="All Blocks"
          options={[{ value: '', label: 'All Blocks' }, { value: 'A', label: 'Block A' }, { value: 'B', label: 'Block B' }, { value: 'C', label: 'Block C' }]}
        />
        <SearchableSelect
          className="finput" style={{ width: 180 }} value={cap} onChange={setCap}
          placeholder="All Capacities"
          options={[{ value: '', label: 'All Capacities' }, { value: '10', label: '10 pax' }, { value: '20', label: '20 pax' }, { value: '40', label: '40 pax' }]}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: '.48rem', background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 'var(--rs)', padding: '.42rem .9rem', flex: 1, maxWidth: 260 }}>
          <i className="fa-solid fa-magnifying-glass" style={{ color: 'var(--slate-lt)', fontSize: '.88rem' }} />
          <input type="text" placeholder="Search rooms…" value={q} onChange={(e) => setQ(e.target.value)} style={{ background: 'none', color: 'var(--ink)', fontSize: 'var(--text-sm)', flex: 1 }} />
        </div>
        <button className="btn btn-primary" style={{ marginLeft: 'auto' }} onClick={() => show('Add room — coming soon', 'info')}><i className="fa-solid fa-plus" /> Add Room</button>
      </div>
      <div className="g4" style={{ marginBottom: '1.5rem' }}>
        <div className="sc" style={{ borderTopColor: 'var(--ocean)' }}><div className="sv" style={{ color: 'var(--ocean)' }}>{CLASSROOMS.length}</div><div className="sl">Total Classrooms</div><div className="ss">A01–A13 · B01–B11 · C01–C08</div></div>
        <div className="sc" style={{ borderTopColor: 'var(--sea-bg)' }}><div className="sv" style={{ color: 'var(--ocean)' }}>{CLASSROOMS.filter((r) => r.cap === 10).length}</div><div className="sl">10-pax Rooms</div><div className="ss">Block A (A04–A13) + Block B</div></div>
        <div className="sc" style={{ borderTopColor: 'var(--violet)' }}><div className="sv" style={{ color: 'var(--ocean)' }}>{CLASSROOMS.filter((r) => r.cap === 20).length}</div><div className="sl">20-pax Rooms</div><div className="ss">Block C (C01–C08)</div></div>
        <div className="sc" style={{ borderTopColor: 'var(--gold-bg)' }}><div className="sv" style={{ color: 'var(--ocean)' }}>{CLASSROOMS.filter((r) => r.cap === 40).length}</div><div className="sl">40-pax Rooms</div><div className="ss">Block A (A01–A03)</div></div>
      </div>

      {['A', 'B', 'C'].map((b) => {
        const rooms = filtered.filter((r) => r.block === b);
        if (!rooms.length) return null;
        const avgUtil = Math.round(rooms.reduce((a, r) => a + r.util, 0) / rooms.length);
        return (
          <div key={b} style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.85rem', marginBottom: '1rem', padding: '.65rem 1rem', background: 'var(--surface)', borderRadius: 'var(--r)', border: '1px solid var(--border)', borderLeft: `4px solid ${BLOCK_COL[b]}` }}>
              <i className="fa-solid fa-building" style={{ color: BLOCK_COL[b], fontSize: '1.05rem' }} />
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.82rem', fontWeight: 700, color: BLOCK_COL[b] }}>{BLOCK_LABEL[b]}</div>
              <span style={{ marginLeft: 'auto', background: 'var(--cloud)', border: '1px solid var(--border)', borderRadius: 999, padding: '.15rem .65rem', fontSize: '.72rem', fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--slate)' }}>{rooms.length} rooms · {avgUtil}% avg util</span>
            </div>
            <div className="g3">
              {rooms.map((r) => (
                <div key={r.id} className="room-card">
                  {/* r.col is a fixed pale tint (doesn't flip with theme) — the icon
                      was inheriting var(--ink), which goes near-white in dark mode
                      and disappears against it. r.colDk is the matching vivid accent
                      already in the data for exactly this, just never wired up. */}
                  <div className="rm-th" style={{ background: r.col, color: r.colDk }}>
                    <i className={`fa-solid ${r.icon}`} />
                    <div className="rm-id">{r.id}</div>
                    <div style={{ position: 'absolute', top: '.6rem', right: '.68rem' }}><span className={`pill ${r.util >= 90 ? 'p-conf' : r.util >= 75 ? 'p-lock' : 'p-sea'}`}>{r.util}% used</span></div>
                  </div>
                  <div className="rm-body">
                    <div className="rm-n">{r.name}</div>
                    <div className="rm-m">
                      <span><i className="fa-solid fa-users" style={{ color: 'var(--slate-lt)' }} /> {r.cap} pax</span>
                      <span><i className="fa-solid fa-building" style={{ color: 'var(--slate-lt)' }} /> {r.campus}</span>
                    </div>
                    <div className="rm-eq">{r.equip.map((e) => <span key={e} className="eq-tag">{e}</span>)}</div>
                    <div className="pt-track"><div className={`pt-fill ${r.util >= 90 ? 'pf-coral' : r.util >= 75 ? 'pf-gold' : 'pf-sea'}`} style={{ width: `${r.util}%` }} /></div>
                  </div>
                  <div className="rm-foot">
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--slate)' }}>Util: <strong style={{ color: r.util >= 85 ? 'var(--red)' : 'var(--ink)' }}>{r.util}%</strong></span>
                    <div style={{ display: 'flex', gap: '.4rem' }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => show(`${r.name} schedule — coming soon`, 'info')}><i className="fa-solid fa-calendar-week" /></button>
                      <button className="btn btn-outline btn-sm" onClick={() => show(`Editing ${r.name} — coming soon`, 'info')}><i className="fa-solid fa-pencil" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
